import type { Component, ComponentType, RefObject } from "react";
import { createElement } from "react";
import { findDOMNode, render, unmountComponentAtNode } from "react-dom";

import type { IErrorContainer } from "../ErrorContainer";
import { ErrorContainer } from "../ErrorContainer";
import type { IErrorMessage } from "../ErrorMessage";
import { ErrorMessage } from "../ErrorMessage";
import type { IValidator } from "../Validator";
import { Validator } from "../Validator";

import * as css from "./ErrorHandler.module.css";

export enum ErrorType {
    Error,
    Validator,
}

export interface IErrorComponent extends Component {
    errors: IErrors;
}

export type IErrors = {
    [key: string]: IError;
};

export interface IError<T extends Component = Component> {
    name: string;
    type: ErrorType;
    container: IErrorContainer;
    errorMessage?: IErrorMessage | IValidator;
    targetRef?: string | string[];
    targetRefObj?: RefObject<HTMLElement | T> | Array<RefObject<HTMLElement | T>>;
    disabled?: boolean;
    onValidate: () => boolean;
}

export type RenderErrorCallback = (error: IError, errorMessage: IErrorMessage) => ComponentType;

function findErrorContainer(component: IErrorComponent, error: IError | null) {
    if (error?.container?.targetRefObj?.current) {
        return error?.container?.targetRefObj?.current;
    }

    if (!component || !component.refs) {
        return null;
    }

    if (!error) {
        return null;
    }

    let container: ErrorContainer | null = null;

    for (const ref in component.refs) {
        if (ref === error.container.ref) {
            container =
                component.refs[ref] instanceof ErrorContainer
                    ? (component.refs[ref] as ErrorContainer)
                    : null;
            if (container) {
                break;
            }
        }
    }

    return container;
}

export function displayError(
    component: IErrorComponent,
    error: IError | null,
    errorMessages?: object,
    renderErrorCallback?: RenderErrorCallback,
) {
    const container = findErrorContainer(component, error);

    if (!error) {
        // tslint:disable-next-line:no-console
        console.error("Error cannot be null or undefined");
        return;
    }

    if (!container) {
        // tslint:disable-next-line:no-console
        console.error("Missing error container for error", error);
        return;
    }

    const _obj: { [index: string]: any } = errorMessages || component.props;

    if (!_obj) {
        // tslint:disable-next-line:no-console
        console.error("No props received or found in component", component);
        return;
    }

    setTarget(component, error, true);

    let { errorMessage } = error;
    if (!errorMessage) {
        for (const propName of Object.keys(_obj)) {
            const prop = _obj[propName] as IErrorMessage;
            if (prop && prop.errorName === error.name) {
                errorMessage = prop;
                break;
            }
        }
    }

    if (!errorMessage) {
        // tslint:disable-next-line:no-console
        console.error("Couldn't find correspondent error message to error ", error);
        return;
    }

    let e: JSX.Element | null = null;
    if (renderErrorCallback) {
        const c = renderErrorCallback.call(component, error, errorMessage);
        if (c) {
            e = createElement(c, errorMessage);
        }
    }

    if (!e) {
        if (error.type === ErrorType.Error) {
            e = <ErrorMessage {...errorMessage} />;
        }

        if (error.type === ErrorType.Validator) {
            e = <Validator {...errorMessage} />;
        }
    }

    render(e, findDOMNode(container) as Element);
}

// export function displayErrorMessage(component: IErrorComponent, errorMessage: IErrorMessage): void;
// export function displayErrorMessage(component: IErrorComponent, errorMessage: IErrorMessage, renderErrorCallback: RenderErrorCallback): void;
export function displayErrorMessage(
    component: IErrorComponent,
    errorMessage: IErrorMessage,
    renderErrorCallback?: RenderErrorCallback,
) {
    if (!errorMessage) {
        // tslint:disable-next-line:no-console
        console.error("Error message cannot be null or undefined");
        return;
    }

    let error: IError | null = null;
    let e_default: IError | null = null;

    for (const _error of Object.keys(component.errors)) {
        const e = component.errors[_error];

        if (_error === "default") {
            e_default = e;
        }

        if (e && e.name === errorMessage.errorName) {
            error = e;
            break;
        }
    }

    if (!error && !e_default) {
        // tslint:disable-next-line:no-console
        console.error("No error found in component for error message", errorMessage);
        return;
    }
    if (!error) {
        error = e_default;
    }

    if (!errorMessage.errorName) {
        errorMessage.errorName = "default";
    }

    displayError(component, error, { errorMessage }, renderErrorCallback);
}

export function displayAllErrors(component: IErrorComponent, renderErrorCallback?: RenderErrorCallback) {
    if (!component || !component.errors) {
        return;
    }

    for (const errorName of Object.keys(component.errors)) {
        const error = component.errors[errorName];
        displayError(component, error, renderErrorCallback);
    }
}

export function clearError(component: IErrorComponent, error: IError) {
    const container = findErrorContainer(component, error);

    if (!container) {
        return;
    }

    setTarget(component, error, false);

    unmountComponentAtNode(findDOMNode(container) as Element);
}

export function clearAllErrors(component: IErrorComponent) {
    if (!component || !component.errors) {
        return;
    }

    for (const errorName of Object.keys(component.errors)) {
        const error = component.errors[errorName];
        clearError(component, error);
    }
}

function setTarget(component: IErrorComponent, error: IError, set: boolean) {
    if (error.targetRefObj) {
        if (error.targetRefObj instanceof Array) {
            error.targetRefObj.forEach((val) => setObjectRefTarget(val, set));
        } else {
            setObjectRefTarget(error.targetRefObj, set);
        }

        return;
    }

    if (error.targetRef && error.targetRef instanceof Array) {
        for (const t of error.targetRef) {
            setStringTarget(component, t, set);
        }
    } else if (error.targetRef) {
        setStringTarget(component, error.targetRef as string, set);
    }
}

function setObjectRefTarget(target: RefObject<any>, set: boolean) {
    const element: HTMLElement = findDOMNode(target.current) as HTMLElement;

    if (element) {
        if (set) {
            element.classList.add(css.hasError);
        } else {
            element.classList.remove(css.hasError);
        }
    }
}

function setStringTarget(component: IErrorComponent, ref: string, set: boolean) {
    if (!component || !component.refs || !ref) {
        return;
    }

    if (!component.refs[ref]) {
        return;
    }

    const target = findDOMNode(component.refs[ref]) as Element;

    if (!target) {
        return;
    }

    if (set) {
        target.classList.add(css.hasError);
    }

    if (!set) {
        target.classList.remove(css.hasError);
    }
}

export function setValidate(component: IErrorComponent, error: IError): void {
    if (!error) {
        // tslint:disable-next-line:no-console
        console.error("Error cannot be null or undefined");
        return;
    }

    displayError(component, error, { ...component.props });
}

// export function validate(component: IErrorComponent, error: IError): boolean;
// export function validate(component: IErrorComponent, error: IError, renderErrorCallback: RenderErrorCallback): boolean;
export function validate(
    component: IErrorComponent,
    error: IError,
    renderErrorCallback?: RenderErrorCallback,
): boolean {
    if (!error) {
        // tslint:disable-next-line:no-console
        console.error("Error cannot be null or undefined");
        return false;
    }

    if (error.disabled === true) {
        return true;
    }

    if (!error.onValidate) {
        // tslint:disable-next-line:no-console
        console.error("Method 'onValidate' cannot be null or undefined in error", error);
        return false;
    }

    const result = error.onValidate.call(component);

    if (!result) {
        displayError(component, error, { ...component.props }, renderErrorCallback);
    }

    return result;
}

// export function validateComponent(component: IErrorComponent): boolean;
// export function validateComponent(component: IErrorComponent, validationSuccess: () => void): boolean;
// export function validateComponent(
//     component: IErrorComponent,
//     validationSuccess: () => void,
//     validationFailed: (errors: IErrorMessage[]) => void
// ): boolean;
// export function validateComponent(
//     component: IErrorComponent,
//     validationSuccess: () => void,
//     validationFailed: (errors: IErrorMessage[]) => void,
//     renderErrorCallback: RenderErrorCallback
// ): boolean;
export function validateComponent(
    component: IErrorComponent,
    validationSuccess?: () => void,
    validationFailed?: (errors: IErrorMessage[]) => void,
    renderErrorCallback?: RenderErrorCallback,
): boolean {
    if (!component || !component.errors) {
        return true;
    }

    const failed: IError[] = [];

    for (const errorName of Object.keys(component.errors)) {
        const error = component.errors[errorName];

        const result = validate(component, error, renderErrorCallback);

        if (!result) {
            failed.push(error);
        }
    }

    if (failed.length === 0 && validationSuccess) {
        validationSuccess.call(component);
    }

    if (failed.length > 0 && validationFailed) {
        validationFailed.call(component, failed);
    }

    return failed.length === 0;
}
